import { computed, inject, provide, ref } from 'vue';
import {
  type IMailboxSharedActionDialogMutationFn,
  MAILBOX_SHARED_ACTIONS_DIALOG_KEY,
  MAILBOX_SHARED_AVATAR_INJECT_KEY,
  MAILBOX_SHARED_CONTACT_INJECT_KEY,
  MAILBOX_SHARED_DETAILS_INJECT_KEY,
  MAILBOX_SHARED_FOLDERS_INJECT_KEY
} from '@/types/components/mailbox-shared/UseMailboxSharedProvides.const';
import useFoldersService from '@/composables/apiServices/useFoldersService';
import useUserService from '@/composables/apiServices/useUserService';
import useAvatarService from '@/composables/apiServices/useAvatarService';
import useContactsService from '@/composables/apiServices/useContactsService';

const useMailboxSharedProvides = () => {
  const provideMailboxActionDialog = () => {
    const actionDialogText = ref<string | undefined>(undefined);
    const actionDialogLabel = ref<string | undefined>(undefined);
    let callbackFn: IMailboxSharedActionDialogMutationFn | undefined = undefined;

    const isActionDialogOpen = computed<boolean>({
      get: () => !!actionDialogText.value && !!actionDialogLabel.value,
      set: (value: boolean): void => {
        if (!value) {
          callbackFn = undefined;
          actionDialogText.value = undefined;
          actionDialogLabel.value = undefined;
        }
      }
    });

    const handleDialogMutation = () => {
      if (isActionDialogOpen.value && callbackFn) {
        callbackFn();
        isActionDialogOpen.value = false;
      }
    };

    provide(MAILBOX_SHARED_ACTIONS_DIALOG_KEY, {
      setDialogText: (label: string, text: string, callback) => {
        actionDialogLabel.value = label;
        actionDialogText.value = text;
        callbackFn = callback;
      }
    });

    return {
      isActionDialogOpen,
      actionDialogText,
      actionDialogLabel,
      handleDialogMutation
    };
  };

  const injectMailboxActionDialog = () => {
    const context = inject(MAILBOX_SHARED_ACTIONS_DIALOG_KEY);
    if (!context) {
      throw new Error('[UseMailboxSharedProvides] - Action dialog context not injected');
    }

    return context;
  };
  const provideMailboxFlatFolders = () => {
    const { getFlatFolders } = useFoldersService();
    const {
      data: flatFolders,
      suspense: flatFoldersSuspense,
      isLoading: isFlatFoldersLoading
    } = getFlatFolders();

    provide(MAILBOX_SHARED_FOLDERS_INJECT_KEY, { flatFolders, isFlatFoldersLoading });
    return { flatFolders, flatFoldersSuspense, isFlatFoldersLoading };
  };

  const injectMailboxFlatFolders = () => {
    const context = inject(MAILBOX_SHARED_FOLDERS_INJECT_KEY);
    if (!context) {
      throw new Error('[UseMailboxSharedProvides] - Flat folders context not injected');
    }

    return context;
  };
  const provideMailboxUserDetails = () => {
    const { details, attributes } = useUserService();
    const { data: userDetails, suspense: userDetailsSuspense } = details();
    const { data: userAttributes, suspense: userAttributesSuspense } = attributes();

    provide(MAILBOX_SHARED_DETAILS_INJECT_KEY, {
      userDetails,
      userDetailsSuspense,
      userAttributes,
      userAttributesSuspense
    });
    return { userDetails, userDetailsSuspense, userAttributes, userAttributesSuspense };
  };
  const injectMailboxUserDetails = () => {
    const context = inject(MAILBOX_SHARED_DETAILS_INJECT_KEY);
    if (!context) {
      throw new Error('[UseMailboxSharedProvides] - User details context not injected');
    }

    return context;
  };

  const provideMailboxUserAvatar = () => {
    const { getAvatar } = useAvatarService();
    const { data: avatarData } = getAvatar();

    provide(MAILBOX_SHARED_AVATAR_INJECT_KEY, avatarData);
    return { avatarData };
  };
  const injectMailboxUserAvatar = () => {
    const context = inject(MAILBOX_SHARED_AVATAR_INJECT_KEY);
    if (!context) {
      throw new Error('[UseMailboxSharedProvides] - User avatar context not injected');
    }

    return context;
  };

  const provideMailboxContactsList = () => {
    const { getContactsList } = useContactsService();
    const { data: contactsData } = getContactsList();
    const emailMappedContacts = computed(() => {
      if (!contactsData.value) {
        return {};
      }
      const mappedContacts: Record<string, (typeof contactsData.value)[0]> = {};
      contactsData.value.forEach((contact) => {
        contact.emails.forEach((email) => {
          mappedContacts[email] = contact;
        });
      });
      return mappedContacts;
    });

    provide(MAILBOX_SHARED_CONTACT_INJECT_KEY, { contactsData, emailMappedContacts });
    return { contactsData, emailMappedContacts };
  };
  const injectMailboxContactsList = () => {
    const context = inject(MAILBOX_SHARED_CONTACT_INJECT_KEY);
    if (!context) {
      throw new Error('[UseMailboxSharedProvides] - Contact list context not injected');
    }

    return context;
  };

  return {
    provideMailboxFlatFolders,
    injectMailboxFlatFolders,
    provideMailboxUserDetails,
    injectMailboxUserDetails,
    provideMailboxUserAvatar,
    injectMailboxUserAvatar,
    provideMailboxContactsList,
    injectMailboxContactsList,
    provideMailboxActionDialog,
    injectMailboxActionDialog
  };
};

export default useMailboxSharedProvides;
